import ShowChartIcon from "@mui/icons-material/ShowChart";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, type ReactElement, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseLineChart, LINE_CHART_PALETTE } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import type { InHouseLineDataGroup } from "../../../components/UIToolkit/Graphs/LineChart/types";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { fetchedDataToChartDataGroup } from "../../ManagementCenter/utils";
import { usePbjHoursByRoleDaily } from "../hooks/usePbjAudit";
import { sectionCardSx } from "../PredictionTab/colors";
import { ROLE_GROUP_ORDER, RoleFilterPanel } from "./RoleFilterPanel";

type Props = {
	/** Committed (immutable) audit range — mirrors the other AuditTab sub-tabs' prop pattern. */
	startDate: string;
	endDate: string;
};

// Height that gives many role lines room to breathe inside the audit card. The tree replaces the
// wrapping legend, so no extra legend headroom is needed.
const CHART_HEIGHT = "460px";

/**
 * Orders every role label deterministically: by group (fixed ROLE_GROUP_ORDER), then alphabetically
 * within a group, then any role not present in `roleGroups` appended alpha-last. This single order
 * drives BOTH the palette cycling and the tree, so a role's color dot always matches its chart line.
 */
function orderRolesStably(hoursByRole: Record<string, unknown>, roleGroups: Record<string, string[]>): string[] {
	const seen = new Set<string>();
	const ordered: string[] = [];
	for (const groupKey of ROLE_GROUP_ORDER) {
		const roles = [...(roleGroups[groupKey] ?? [])].sort((a, b) => a.localeCompare(b));
		for (const role of roles) {
			if (!seen.has(role)) {
				seen.add(role);
				ordered.push(role);
			}
		}
	}
	// Defensive: any hoursByRole key not covered by roleGroups still gets a stable slot + color.
	for (const role of Object.keys(hoursByRole).sort((a, b) => a.localeCompare(b))) {
		if (!seen.has(role)) {
			seen.add(role);
			ordered.push(role);
		}
	}
	return ordered;
}

/**
 * Every calendar day (ISO YYYY-MM-DD) in the inclusive committed range. Used to densify each role's
 * series to 0 on idle days: the backend emits a date key only for days a role actually reported hours,
 * so without this the time-axis line bridges straight across off-days (weekends read as if the value
 * held), hiding real dips. Filling 0 makes the line correctly drop to 0 on days with no reported hours.
 */
function enumerateIsoDates(startDate: string, endDate: string): string[] {
	const start = DateTime.fromISO(startDate);
	const end = DateTime.fromISO(endDate);
	if (!start.isValid || !end.isValid || end < start) return [];
	const dates: string[] = [];
	for (let day = start; day <= end; day = day.plus({ days: 1 })) {
		const iso = day.toISODate();
		if (iso) dates.push(iso);
	}
	return dates;
}

/**
 * Audit "Hours by Role" — a time-series line chart of total reported (net-of-breaks) hours per day,
 * one line per PBJ role. A grouped role-filter tree replaces the legend: it drives controlled line
 * visibility and its color dots mirror the chart line colors via a shared, deterministic palette map.
 */
export const HoursByRoleChart: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { timezone } = useHospitalData();
	const { data, isLoading, isError } = usePbjHoursByRoleDaily(startDate, endDate);

	const [hiddenRoles, setHiddenRoles] = useState<Set<string>>(new Set());
	const [panelCollapsed, setPanelCollapsed] = useState(false);

	// Stable role order + role->color map, shared by the chart lines and the tree dots.
	const { orderedRoles, roleColor } = useMemo(() => {
		if (!data) return { orderedRoles: [] as string[], roleColor: {} as Record<string, string> };
		const roles = orderRolesStably(data.hoursByRole, data.roleGroups);
		const color: Record<string, string> = {};
		roles.forEach((role, idx) => {
			color[role] = LINE_CHART_PALETTE[idx % LINE_CHART_PALETTE.length];
		});
		return { orderedRoles: roles, roleColor: color };
	}, [data]);

	const roleTotalHours = useMemo<Record<string, number>>(() => {
		if (!data) return {};
		const totals: Record<string, number> = {};
		for (const [role, dateMap] of Object.entries(data.hoursByRole)) {
			totals[role] = Object.values(dateMap).reduce((sum, v) => sum + v, 0);
		}
		return totals;
	}, [data]);

	const dataGroups = useMemo<InHouseLineDataGroup[] | undefined>(() => {
		if (!data) return undefined;
		// Densify every role to 0 across each committed day so the line dips to 0 on idle days instead
		// of bridging the gap (a missing day would otherwise read as if the neighboring value held).
		const allDates = enumerateIsoDates(startDate, endDate);
		// Emit in the stable order so line stacking order is deterministic; explicit color keeps
		// each line's hue locked to its tree dot regardless of object-key iteration order.
		return orderedRoles.map((role) => {
			const sparse = data.hoursByRole[role];
			const dense: Record<string, number> = allDates.length > 0 ? Object.fromEntries(allDates.map((iso) => [iso, sparse[iso] ?? 0])) : sparse;
			return fetchedDataToChartDataGroup({ data: dense, name: role, timezone, color: roleColor[role] });
		});
	}, [data, orderedRoles, roleColor, timezone, startDate, endDate]);

	const toggleRole = useCallback((role: string, visible: boolean) => {
		setHiddenRoles((prev) => {
			const next = new Set(prev);
			if (visible) next.delete(role);
			else next.add(role);
			return next;
		});
	}, []);

	const toggleGroup = useCallback(
		(groupKey: string, visible: boolean) => {
			const roles = data?.roleGroups[groupKey] ?? [];
			setHiddenRoles((prev) => {
				const next = new Set(prev);
				for (const role of roles) {
					if (visible) next.delete(role);
					else next.add(role);
				}
				return next;
			});
		},
		[data],
	);

	const selectAllRoles = useCallback(() => setHiddenRoles(new Set()), []);
	const clearAllRoles = useCallback(() => setHiddenRoles(new Set(orderedRoles)), [orderedRoles]);

	// The filter tree replaces the legend ONLY when it has something to render, and it renders nothing
	// but grouped roles (RoleFilterPanel filters ROLE_GROUP_ORDER to groups with members). On the PBJ
	// Audit actuals fallback the backend deliberately omits `roleGroups` — those departments have no
	// mapped CMS job codes, so inventing a grouping would file real RN hours under "Support and Other" —
	// which leaves the tree empty. Hiding the chart's own legend as well would then draw 2-11 unlabelled
	// coloured lines with no way to tell them apart or toggle one, so keep the legend in that case.
	const hasRoleGroups = Object.keys(data?.roleGroups ?? {}).length > 0;

	// The chart is controlled by the tree (hideLegend hides the chart's own toggle affordance), but the
	// controlled contract still expects a toggle callback — flip the role so a click can never desync.
	const onSeriesVisibilityToggle = useCallback((role: string) => {
		setHiddenRoles((prev) => {
			const next = new Set(prev);
			if (next.has(role)) next.delete(role);
			else next.add(role);
			return next;
		});
	}, []);

	// One wrapper for every state, so the error/empty reads inside the same titled card as the chart
	// (the other Audit sub-tabs each render their own section card — this one is no exception).
	const section = (body: ReactNode): ReactElement => (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.hours_by_role.section.title")}
			description={t("pbj_model.audit.hours_by_role.section.subtitle")}
			dataTestId="pbj-hours-by-role-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			{body}
		</SettingsSection>
	);

	if (isError) {
		return section(<EmptyState title={t("pbj_model.audit.hours_by_role.failed")} icon={<ShowChartIcon />} dataTestId="pbj-hours-by-role-error" />);
	}

	// Empty (zero roles): the endpoint succeeded but no reported hours in range — a meaningful clean result.
	if (!isLoading && dataGroups && dataGroups.length === 0) {
		return section(<EmptyState title={t("pbj_model.audit.hours_by_role.empty")} icon={<ShowChartIcon />} dataTestId="pbj-hours-by-role-empty" />);
	}

	return section(
		<Box data-testid="pbj-hours-by-role-chart" sx={{ display: "flex", alignItems: "flex-start", gap: 2, width: "100%" }}>
			{/* Chart flexes to fill remaining width; while loading `dataGroups` is undefined → chart renders its own skeleton. */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<InHouseLineChart
					width="100%"
					height={CHART_HEIGHT}
					margin={{ top: 4, right: 8, bottom: 0, left: 20 }}
					leftAxisData={{
						dataGroups,
						minValue: 0,
						headerLabel: t("pbj_model.audit.hours_by_role.y_axis"),
						tickRenderer: (value) => `${value.toFixed(0)}h`,
					}}
					tooltipValueFormatter={(value) => `${value.toFixed(2)}h`}
					hideZeroInTooltip
					hideLegend={hasRoleGroups}
					hiddenSeries={hiddenRoles}
					onSeriesVisibilityToggle={onSeriesVisibilityToggle}
				/>
			</Box>

			{/* Filter tree replaces the legend. Rendered once role data has loaded. */}
			{data && orderedRoles.length > 0 && (
				<RoleFilterPanel
					roleGroups={data.roleGroups}
					allRoles={orderedRoles}
					roleColor={roleColor}
					roleTotalHours={roleTotalHours}
					hiddenRoles={hiddenRoles}
					onToggleRole={toggleRole}
					onToggleGroup={toggleGroup}
					onSelectAll={selectAllRoles}
					onClearAll={clearAllRoles}
					collapsed={panelCollapsed}
					onCollapsedChange={setPanelCollapsed}
					dataTestId="pbj-hours-by-role-filter"
				/>
			)}
		</Box>,
	);
};
